import { z } from 'zod';
import type { FeatureKey } from './features';

// Single source of truth for the privileged-API permission taxonomy (auth
// layer, token/OAuth minting, settings UI, OpenAPI). See .ai/plans/api-tokens.
export const API_SCOPES = [
  'events:write',
  'identities:write',
  'groups:write',
  'consent:write',
  'companies:write',
  'actions:write',
  'events:read',
  'analytics:read',
  'revenue:read',
  'identities:read',
  'actions:read',
  'privacy_requests:read',
  'privacy_requests:write',
  'projects:read',
  'organization:read',
] as const;
export type ApiScope = (typeof API_SCOPES)[number];

// Scopes that are a plan feature when used by a MACHINE credential (API token or
// OAuth client). Unmapped scopes (ingest, privacy requests) work on every plan.
export const SCOPE_FEATURE: Partial<Record<ApiScope, FeatureKey>> = {
  'companies:write': 'apiAccess',
  'actions:write': 'apiAccess',
  'actions:read': 'apiAccess',
  'events:read': 'apiAccess',
  'analytics:read': 'apiAccess',
  'revenue:read': 'apiAccess',
  'identities:read': 'apiAccess',
  'projects:read': 'apiAccess',
  'organization:read': 'apiAccess',
};
export const apiScopeSchema = z.enum(API_SCOPES);

// The only scopes an MCP client may request: those backed by an MCP tool. Ingest
// and privacy-request scopes belong to machine credentials, never to an assistant.
export const MCP_SCOPES = [
  'analytics:read',
  'events:read',
  'revenue:read',
  'identities:read',
  'projects:read',
  'companies:write',
  'actions:write',
  'actions:read',
] as const satisfies readonly ApiScope[];
export type McpScope = (typeof MCP_SCOPES)[number];

export type ApiScopeDef = {
  scope: ApiScope;
  label: string;
  description: string;
};

export type ApiScopeGroup = {
  label: string;
  scopes: readonly ApiScopeDef[];
};

// Grouped for the token-creation UI (grouped checkboxes) and the docs scope
// table. Every scope in API_SCOPES appears exactly once across these groups.
export const SCOPE_GROUPS: readonly ApiScopeGroup[] = [
  {
    label: 'Ingest',
    scopes: [
      {
        scope: 'events:write',
        label: 'Write Events',
        description: 'Send events server-side via /track.',
      },
      {
        scope: 'identities:write',
        label: 'Write Identities',
        description: 'Identify users server-side via /identify.',
      },
      {
        scope: 'groups:write',
        label: 'Write Groups',
        description: 'Associate users with groups via /group.',
      },
      {
        scope: 'consent:write',
        label: 'Write Consent',
        description: 'Record consent server-side via /consent.',
      },
    ],
  },
  {
    label: 'Read Data',
    scopes: [
      {
        scope: 'events:read',
        label: 'Read Events',
        description:
          'Search raw events via /search-events, including any amounts sent on them.',
      },
      {
        scope: 'analytics:read',
        label: 'Read Analytics',
        description:
          'Read analytics counts, aggregates, and metrics. Property totals can add up amounts sent on events.',
      },
      {
        scope: 'revenue:read',
        label: 'Read Revenue',
        description:
          'Read revenue metrics, attribution, and cohorts, plus the revenue on company and user metrics.',
      },
      {
        scope: 'identities:read',
        label: 'Read Identities',
        description:
          'Read identity profiles, sessions, and activity. Includes personal data.',
      },
      {
        scope: 'actions:read',
        label: 'Read Actions',
        description: 'List saved actions and the action catalog.',
      },
    ],
  },
  {
    label: 'Privacy Requests',
    scopes: [
      {
        scope: 'privacy_requests:read',
        label: 'Read Privacy Requests',
        description:
          'Read the status and results of privacy requests (deletion and export, also called DSR/DSAR).',
      },
      {
        scope: 'privacy_requests:write',
        label: 'File Privacy Requests',
        description:
          'File deletion and export (DSAR) privacy requests for the organization.',
      },
    ],
  },
  {
    label: 'Organization',
    scopes: [
      {
        scope: 'projects:read',
        label: 'Read Projects',
        description: "List the organization's projects.",
      },
      {
        scope: 'organization:read',
        label: 'Read Organization',
        description: 'Read organization members and settings metadata.',
      },
    ],
  },
  {
    label: 'Configuration',
    scopes: [
      {
        scope: 'companies:write',
        label: 'Manage Companies & Target Accounts',
        description: 'Create Target Accounts and update company profiles.',
      },
      {
        scope: 'actions:write',
        label: 'Create & Run Actions',
        description: 'Create saved actions and run them (running may spend credits).',
      },
    ],
  },
];

export const API_SCOPE_BY_KEY: Record<ApiScope, ApiScopeDef> =
  Object.fromEntries(
    SCOPE_GROUPS.flatMap((group) =>
      group.scopes.map((scope) => [scope.scope, scope]),
    ),
  ) as Record<ApiScope, ApiScopeDef>;

// What a credential can reach. A single-project token is just `projects` with
// one id; the authorizer treats one and many identically.
export type ResourceScope =
  | { type: 'organization' }
  | { type: 'projects'; projectIds: string[] };

export const resourceScopeSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('organization') }),
  z.object({
    type: z.literal('projects'),
    projectIds: z.array(z.string().min(1)).min(1),
  }),
]);

// Shape stored in the api-key row's `metadata` (our authorizer reads it back).
export type ApiTokenMetadata = {
  scopes: ApiScope[];
  resourceScope: ResourceScope;
  createdBy: string;
};

// A token as shown in the settings list. Never carries the secret value.
export type ApiTokenView = {
  id: string;
  name: string | null;
  start: string | null;
  prefix: string | null;
  scopes: ApiScope[];
  resourceScope: ResourceScope;
  createdBy: string | null;
  createdAt: string;
  lastRequest: string | null;
  expiresAt: string | null;
  enabled: boolean;
};

// The one-time create response: an ApiTokenView plus the full secret.
export type CreatedApiToken = ApiTokenView & { key: string };

export const createApiTokenSchema = z.object({
  name: z.string().trim().min(1).max(120),
  resourceScope: resourceScopeSchema,
  scopes: z.array(apiScopeSchema).min(1),
  // Seconds until expiry, or null for a non-expiring token. Capped at ~400 days.
  expiresIn: z
    .number()
    .int()
    .min(1)
    .max(400 * 24 * 60 * 60)
    .nullable()
    .default(null),
});
export type CreateApiTokenInput = z.infer<typeof createApiTokenSchema>;

// A legacy `sk_` key's frozen abilities: what it can do today, minus every org
// scope, so normalizing it onto the new layer never widens its reach.
export const LEGACY_SK_SCOPES: readonly ApiScope[] = [
  'events:write',
  'identities:write',
  'groups:write',
  'consent:write',
  'events:read',
  'analytics:read',
];

// An organization-owned OAuth client (client_credentials) as listed in Integrations → AI & API Access.
export type OAuthClientView = {
  // The OAuth client_id.
  id: string;
  name: string | null;
  // The server-authorized ceiling a token may request.
  scopes: ApiScope[];
  createdAt: string | null;
  disabled: boolean;
};

export type CreatedOAuthClient = OAuthClientView & { clientSecret: string };

export const createOAuthClientSchema = z.object({
  name: z.string().trim().min(1).max(100),
  scopes: z.array(apiScopeSchema).min(1),
});
export type CreateOAuthClientInput = z.infer<typeof createOAuthClientSchema>;
